// The in-room Partnership Form (2026-10-28, Elijah): one form replacing the
// partnership, marketing and onboarding Google Docs. Design and field sources:
// hub .claude/skills/deal-room/references/partnership-form/. Pure data plus the
// required-answer check, shared by the page and the API (submit refuses while
// anything required is missing). The onboarding questions copy the supplier
// portal application's options and limits on purpose (ADR 0002).

export type Answers = Record<string, unknown>;

type Cond = (a: Answers) => boolean;

export interface Field {
    id: string;
    label: string;
    type: "text" | "longtext" | "email" | "phone" | "url" | "number" | "date" | "select" | "multi" | "check" | "repeat";
    options?: readonly string[];
    hint?: string;
    /** Number limits (lead days 1-20, the portal's rule). */
    min?: number;
    max?: number;
    /** Number only: hide the browser's up/down arrows. */
    noSpinner?: boolean;
    /** Number only: whole numbers. */
    integer?: boolean;
    /** Text only: the supplier portal's length cap, so every answer can be posted to its application. */
    maxLength?: number;
    /** A rule against the rest of the scope (the row inside a repeat), e.g. pickup to after pickup from. */
    invalidIf?: Cond;
    required?: boolean | Cond;
    showIf?: Cond;
    /** A sub-heading printed above the field. */
    group?: string;
    /** Repeat only: the row's fields (their `showIf`/`required` see the row) and the minimum row count. */
    fields?: Field[];
    minRows?: number;
    /** Repeat only: open with one row, rows split by a line, each titled with what was typed as its name. */
    flat?: boolean;
}

export interface Section {
    id: string;
    title: string;
    intro?: string;
    fields: Field[];
    /** A rule no single field carries (at least one channel). */
    check?: (a: Answers) => string | null;
}

const YES_NO = ["Yes", "No"] as const;
const PCT = ["1-2%", "2-3%", "3-4%", "4-5%", ">5%"] as const;
const SUPPORT = ["Yes, currently active", "In development / planned", "No, but interested", "No / not sure"] as const;

// The portal's country and state lists (supplier-api CountryHelper / StateQueryService).
export const COUNTRIES = ["United States", "Canada"] as const;
const US_STATES = "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
const CA_PROVINCES = "AB BC MB NB NL NS ON PE QC SK NT NU YT".split(" ");
// The company address asks state before country, so a blank country offers both lists.
export const statesFor = (country: unknown) => (country === "Canada" ? CA_PROVINCES : country === "United States" ? US_STATES : [...US_STATES, ...CA_PROVINCES]);
// The portal's 48 half-hour pickup slots.
export const PICKUP_SLOTS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

const filled = (v: unknown) =>
    v !== undefined && v !== null && v !== "" && v !== false && !(Array.isArray(v) && v.length === 0);

function address(prefix = ""): Field[] {
    return [
        { id: `${prefix}country`, label: "Country", type: "select", options: COUNTRIES, required: true },
        { id: `${prefix}state`, label: "State / province", type: "select", required: true },
        { id: `${prefix}address`, label: "Street address", type: "text", required: true, maxLength: 100 },
        { id: `${prefix}city`, label: "City", type: "text", required: true, maxLength: 50 },
        { id: `${prefix}zip`, label: "Zip / postal code", type: "text", required: true, maxLength: 20 },
    ];
}

// Street first on the company address (Elijah, 2026-11-02); warehouses keep the portal's order.
function companyAddress(): Field[] {
    const by = Object.fromEntries(address().map((f) => [f.id, f]));
    return [{ ...by.address, group: "Company address" }, by.city, by.state, { ...by.zip, label: "ZIP code" }, by.country];
}

const warehouseFields: Field[] = [
    ...address(),
    { id: "pickupFrom", label: "Pickup from", type: "select", options: PICKUP_SLOTS, required: true },
    // The portal refuses a window that does not end after it starts.
    { id: "pickupTo", label: "Pickup to", type: "select", options: PICKUP_SLOTS, required: true, invalidIf: (r) => filled(r.pickupFrom) && String(r.pickupTo) <= String(r.pickupFrom) },
    { id: "contactName", label: "Warehouse contact", type: "text", required: true, maxLength: 50 },
    { id: "contactEmail", label: "Contact email", type: "email", required: true, maxLength: 100 },
    { id: "contactPhone", label: "Contact phone", type: "phone", required: true, maxLength: 50 },
];

// Bison's active top-level catalog categories, in display order (BisonOffice dbo.Categories:
// Depth 1, IsActive, HasActiveProducts; read 2026-11-02).
const BRAND_CATEGORIES = [
    "Furniture", "Rugs & Floor Coverings", "Outdoor & Garden", "Home Decor", "Lighting", "Bedding & Bath", "Baby & Kids", "Pets",
    "Office & School Supplies", "Janitorial & Breakroom", "Automotive Parts", "Sports & Recreation", "Kitchen & Dining", "Other",
] as const;

export const CHANNELS = [
    "Amazon", "Amazon Business", "Walmart", "BisonOffice", "BisonFurniture", "BisonPets", "BisonBaby",
    "Mathis Home", "Macy's", "Lowe's", "BestBuy US", "Kohl's", "eBay", "TikTok Shop",
] as const;
const OPEN = "Open to partnership";
export const NOT_INTERESTED = "Not interested";
export const CHANNEL_STATUS = [OPEN, "Existing 1P relationship", NOT_INTERESTED, "Not applicable"] as const;
export const channelId = (c: string) => `channel_${c.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;

const AD_CHANNELS = [
    "Google Ads / Shopping", "Walmart Connect", "Other marketplace ads (Macy's, Lowe's, etc.)",
    "Social ads (Meta, TikTok, Pinterest)", "Email / CRM", "Influencer / affiliate", "SEO / organic content",
] as const;
const HELP_AREAS = [
    "Marketplace PPC (Amazon, Walmart, Macy's, Lowe's)", "Listing optimization and SEO", "A+ / Enhanced Brand Content",
    "Promotional campaign strategy", "Product launch campaigns", "Affiliate marketing", "Other",
] as const;

export const CONTACT_ROLES = [
    ["am", "Account manager"],
    ["ar", "Accounts receivable"],
    ["fulfillment", "Order handling / fulfillment"],
    ["support", "Customer support"],
] as const;

// An extra contact role is optional, but once started it is filled in
// completely (the portal's rule), unless it is the primary contact again.
// "Same as primary" is a checkbox in the grid: "Yes" when ticked, otherwise empty.
function roleFields(key: string): Field[] {
    const own: Cond = (a) => a[`${key}_same`] !== "Yes";
    const started: Cond = (a) => own(a) && ["name", "title", "email", "phone"].some((f) => filled(a[`${key}_${f}`]));
    return [
        { id: `${key}_same`, label: "Same as primary", type: "select", options: ["Yes"] },
        { id: `${key}_name`, label: "Name", type: "text", showIf: own, required: started, maxLength: 50 },
        { id: `${key}_title`, label: "Title", type: "text", showIf: own },
        { id: `${key}_email`, label: "Email", type: "email", showIf: own, required: started, maxLength: 100 },
        { id: `${key}_phone`, label: "Phone (with extension if any)", type: "phone", showIf: own, required: started, maxLength: 50 },
    ];
}

export const SECTIONS: Section[] = [
    {
        id: "company",
        title: "Company",
        fields: [
            { id: "legalName", label: "Legal company name", type: "text", required: true, maxLength: 100 },
            { id: "dba", label: "DBA / trade name", type: "text", required: true, maxLength: 100 },
            { id: "website", label: "Website", type: "url", required: true, maxLength: 500 },
            ...companyAddress(),
        ],
    },
    {
        id: "brands",
        title: "Brands",
        intro: "List every brand included in this partnership.",
        fields: [
            {
                id: "brands",
                label: "Brand",
                type: "repeat",
                minRows: 1,
                flat: true,
                required: true,
                fields: [
                    { id: "name", label: "Brand name", type: "text", required: true },
                    { id: "category", label: "Category", type: "select", options: BRAND_CATEGORIES, required: true },
                    { id: "categoryOther", label: "What is the other category?", type: "text", showIf: (r) => r.category === "Other", required: true },
                    { id: "trademark", label: "Has trademark?", type: "select", options: YES_NO },
                    { id: "brandRegistry", label: "Amazon Brand Registry completed?", type: "select", options: YES_NO },
                    { id: "directManufacturing", label: "Direct manufacturing?", type: "select", options: YES_NO },
                    { id: "gs1", label: "UPCs registered with GS1 / EAN?", type: "select", options: YES_NO },
                ],
            },
        ],
    },
    {
        id: "channels",
        title: "Channels",
        intro: "Tell us where you stand on each marketplace, so we know why one is left out.",
        // Every channel is answered (2026-10-29, Elijah): a blank one does not say why it was skipped.
        fields: CHANNELS.flatMap((c): Field[] => [
            { id: channelId(c), label: c, type: "select", options: CHANNEL_STATUS, required: true },
            {
                id: `${channelId(c)}_note`,
                label: `Notes on ${c}`,
                hint: `If you would rather we not sell on ${c}, tell us why.`,
                type: "text",
                showIf: (a) => filled(a[channelId(c)]),
                required: (a) => a[channelId(c)] === NOT_INTERESTED,
            },
        ]),
        check: (a) => (CHANNELS.some((c) => a[channelId(c)] === OPEN) ? null : "Mark at least one marketplace open to partnership."),
    },
    {
        id: "sales",
        title: "Sales and returns",
        fields: [
            { id: "revenueRetail", label: "Approximate annual retail (brick and mortar) revenue", type: "select", required: true, options: ["Do not sell through brick and mortar", "Under $1M", "$1M-$5M", "$5M-$20M", "$20M-$100M", "$100M+"] },
            { id: "revenueEcom", label: "Approximate annual e-commerce revenue", type: "select", required: true, options: ["Do not sell on any e-commerce platform", "Under $1M", "$1M-$5M", "$5M-$20M", "$20M-$100M", "$100M+"] },
            { id: "returnRate", label: "Estimated return rate", type: "select", options: PCT, required: true },
            { id: "remorseReturns", label: "Do you accept buyer's remorse returns?", type: "select", options: YES_NO, required: true },
            { id: "damageRate", label: "Estimated damage / defect claim rate", type: "select", options: PCT, required: true },
            { id: "supportingData", label: "Can you share data that supports these numbers?", type: "longtext" },
            { id: "map", label: "Does your brand enforce a MAP (minimum advertised price) policy?", type: "select", options: ["Yes", "No", "Soft MAP"], required: true },
            { id: "mapEnforcement", label: "How do you enforce and monitor MAP across partners?", type: "longtext", showIf: (a) => a.map === "Yes" || a.map === "Soft MAP", required: true },
        ],
    },
    {
        id: "assortment",
        title: "Assortment",
        fields: [
            { id: "skuTotal", label: "Total active SKU count (full catalog)", type: "number", min: 0, required: true, noSpinner: true },
            { id: "skuDropship", label: "Drop-ship / e-commerce ready SKU count", type: "number", min: 0, required: true },
            { id: "priceRange", label: "Approximate retail price range", type: "select", required: true, options: ["Under $30", "$30-$150", "$150-$350", "$350-$750", "$750+", "Mixed, spans several tiers"] },
            { id: "masterFile", label: "Is there a complete master file of product data and imagery for every SKU in scope?", type: "select", required: true, options: ["Yes, all SKUs", "Partial, for some SKUs", "No, it will be ready later"] },
            { id: "masterFileDate", label: "When will it be ready?", type: "date", showIf: (a) => a.masterFile === "No, it will be ready later", required: true },
        ],
    },
    {
        id: "fulfillment",
        title: "Fulfillment",
        fields: [
            { id: "freightSplit", label: "Parcel vs LTL freight split", type: "select", required: true, options: ["All parcel", "All LTL", "Mostly parcel", "Mostly LTL", "50/50", "Other"] },
            { id: "freightSplitOther", label: "Describe your freight split", type: "text", showIf: (a) => a.freightSplit === "Other", required: true },
            { id: "leadParcel", label: "Lead time for small parcels (days from order to ready to ship)", type: "number", min: 1, max: 20, integer: true, required: true },
            { id: "leadLtl", label: "Lead time for LTL shipments (days from order to ready to ship)", type: "number", min: 1, max: 20, integer: true, required: true },
            { id: "carrierAccount", label: "Which carrier account should ship your orders?", type: "select", options: ["Bison Commerce carrier account (preferred)", "Our own carrier account", "Case-by-case"] },
            { id: "packaging", label: "How are your products packaged?", type: "select", options: ["Branded / custom-printed", "Brown / plain box"] },
            { id: "shippingModes", label: "Which shipping modes can you support?", type: "multi", options: ["Cross Dock", "LTL", "Ground"] },
            { id: "warehouses", label: "Warehouse", type: "repeat", minRows: 1, required: true, fields: warehouseFields, group: "Warehouses" },
            { id: "returnsAccepted", label: "Do you accept returns to a return warehouse?", type: "select", options: YES_NO, required: true, group: "Returns" },
            { id: "returnWarehouses", label: "Return warehouse", type: "repeat", minRows: 1, fields: warehouseFields, showIf: (a) => a.returnsAccepted === "Yes", required: true },
        ],
    },
    {
        id: "technology",
        title: "Technology",
        intro: "We start with our supplier portal, so no technical integration is needed to launch. As volume grows we can align on direct integrations.",
        fields: [
            { id: "api", label: "API support", type: "select", options: SUPPORT, required: true },
            { id: "edi", label: "EDI support (850, 855, 856, 810, 846)", type: "select", options: SUPPORT, required: true },
            { id: "ediWhen", label: "When would you like to move to EDI?", type: "select", options: ["Within 0-3 months", "3-6 months", "6-12 months", "12+ months", "No plans currently"], showIf: (a) => filled(a.edi) && a.edi !== SUPPORT[0], required: true },
            { id: "inventoryMethod", label: "How do you send inventory / stock availability updates?", type: "multi", options: ["Email (manual file)", "FTP / SFTP", "EDI (846)", "API"], required: true },
            { id: "inventoryFrequency", label: "Inventory update frequency", type: "select", options: ["Hourly", "Twice daily", "Once daily", "A few times per week", "Weekly"], required: true },
            { id: "connectPreference", label: "How would you like to connect with Bison?", type: "select", options: ["Bison Supplier Portal", "API", "EDI", "Open to guidance"] },
        ],
    },
    {
        id: "marketing",
        title: "Marketing",
        intro: "Help us understand your current marketing and where Bison could support you. This section is optional.",
        fields: [
            { id: "ppc", label: "Do you currently run Amazon PPC (Sponsored Ads)?", type: "select", options: YES_NO, group: "Amazon ads and content" },
            { id: "ppcManager", label: "Who manages it?", type: "select", options: ["Internal", "Agency", "Amazon Ads services", "3P seller"], showIf: (a) => a.ppc === "Yes" },
            { id: "ppcReporting", label: "Do you get regular reporting on ACoS, trends and competitor activity?", type: "select", options: YES_NO, showIf: (a) => a.ppc === "Yes" },
            { id: "listingOwner", label: "Who owns listing optimization (titles, images, A+, Brand Store)?", type: "text" },
            { id: "listingRefresh", label: "How often are listings refreshed?", type: "select", options: ["Monthly", "Quarterly", "Ad hoc", "Never"] },
            { id: "creativeProcess", label: "Do you have a structured creative process (keyword research, testing)?", type: "longtext" },
            ...AD_CHANNELS.flatMap((c, i): Field[] => {
                const k = `ad${i}`;
                return [
                    { id: `${k}_current`, label: "Advertising here today?", type: "select", options: YES_NO, group: c },
                    { id: `${k}_core`, label: "Core to your strategy?", type: "select", options: YES_NO, showIf: (a) => a[`${k}_current`] === "Yes" },
                    { id: `${k}_support`, label: "Want Bison's support?", type: "select", options: YES_NO },
                ];
            }),
            { id: "budget", label: "Approximate monthly marketing budget", type: "select", options: ["Under $2k", "$2k-$5k", "$5k-$10k", "$10k-$25k", "Over $25k", "No set budget"], group: "Budget and team" },
            { id: "contentShare", label: "Share of the advertising budget spent on content creation and optimization (%)", type: "number", min: 0, max: 100 },
            { id: "budgetType", label: "Budget type", type: "select", options: ["Fixed", "Performance-driven", "% of revenue", "Other"] },
            { id: "budgetTypeOther", label: "Describe your budget type", type: "text", showIf: (a) => a.budgetType === "Other" },
            { id: "marketingExecution", label: "Who runs your marketing overall?", type: "select", options: ["Internal team", "Agency", "Mix", "None"] },
            { id: "teamSize", label: "Internal team size", type: "number", min: 0, showIf: (a) => a.marketingExecution === "Internal team" || a.marketingExecution === "Mix" },
            { id: "contentTeam", label: "Do you have an internal content team?", type: "select", options: YES_NO },
            { id: "competitorTracking", label: "Do you track your competition (market share, search rank, pricing)?", type: "select", options: YES_NO },
            { id: "helpAreas", label: "Where could Bison help?", type: "multi", options: HELP_AREAS, group: "Where Bison can help" },
            { id: "helpOther", label: "What else could we help with?", type: "text", showIf: (a) => Array.isArray(a.helpAreas) && a.helpAreas.includes("Other") },
            { id: "marketingGaps", label: "Where do you see gaps in your marketing today?", type: "longtext" },
        ],
    },
    {
        id: "contacts",
        title: "Contacts",
        fields: [
            { id: "primary_name", label: "Name", type: "text", required: true, maxLength: 50 },
            { id: "primary_title", label: "Title", type: "text" },
            { id: "primary_email", label: "Email", type: "email", required: true, maxLength: 100 },
            { id: "primary_phone", label: "Phone (with extension if any)", type: "phone", required: true, maxLength: 50 },
            ...CONTACT_ROLES.flatMap(([k]) => roleFields(k)),
        ],
    },
];

export const visible = (f: Field, a: Answers) => !f.showIf || f.showIf(a);
// A given answer that breaks its own limits counts as missing (lead days 1-20, an email without @,
// more characters than the portal stores).
function invalid(f: Field, v: unknown, scope: Answers = {}): boolean {
    if (!filled(v)) return false;
    if (f.invalidIf?.(scope)) return true;
    if (f.type === "number") {
        const n = Number(v);
        return !Number.isFinite(n) || (!!f.integer && !Number.isInteger(n)) || (f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max);
    }
    if (f.maxLength !== undefined && String(v).length > f.maxLength) return true;
    if (f.type === "email") return !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v));
    return false;
}
export function fieldError(f: Field, v: unknown, scope: Answers = {}): string | null {
    if (!invalid(f, v, scope)) return null;
    if (f.type === "number") return `Enter a ${f.integer ? "whole " : ""}number${f.max !== undefined ? ` from ${f.min ?? 0} to ${f.max}` : ""}`;
    if (f.maxLength !== undefined && String(v).length > f.maxLength) return `Use ${f.maxLength} characters or fewer`;
    return f.type === "email" ? "Enter a valid email" : "Check this answer";
}

const isRequired = (f: Field, a: Answers) => visible(f, a) && (typeof f.required === "function" ? f.required(a) : !!f.required);

/** Labels of the required answers still missing in one section (repeat rows checked field by field). */
export function missingIn(section: Section, a: Answers): string[] {
    const out: string[] = [];
    for (const f of section.fields) {
        if (visible(f, a) && f.type !== "repeat" && invalid(f, a[f.id], a)) {
            out.push(f.label);
            continue;
        }
        if (!isRequired(f, a)) continue;
        if (f.type === "repeat") {
            const rows = Array.isArray(a[f.id]) ? (a[f.id] as Answers[]) : [];
            if (rows.length < (f.minRows ?? 1)) out.push(`at least one ${f.label.toLowerCase()}`);
            rows.forEach((row, i) => {
                for (const sub of f.fields ?? [])
                    if ((isRequired(sub, row) && !filled(row[sub.id])) || invalid(sub, row[sub.id], row)) out.push(`${f.label} ${i + 1}: ${sub.label}`);
            });
        } else if (!filled(a[f.id])) out.push(f.label);
    }
    const rule = section.check?.(a);
    if (rule) out.push(rule);
    return out;
}

export const missingAll = (a: Answers) => SECTIONS.flatMap((s) => missingIn(s, a).map((m) => `${s.title}: ${m}`));

/** Share of required questions answered, 0-100; 100 exactly when the form can be submitted. */
export function formProgress(a: Answers): number {
    const ok = (f: Field, v: unknown, scope: Answers) => isRequired(f, scope) && filled(v) && !invalid(f, v, scope);
    const answered = SECTIONS.flatMap((s) => s.fields).reduce((n, f) => {
        if (!visible(f, a)) return n;
        if (f.type !== "repeat") return n + (ok(f, a[f.id], a) ? 1 : 0);
        if (!isRequired(f, a) || !Array.isArray(a[f.id])) return n;
        return n + (a[f.id] as Answers[]).flatMap((row) => (f.fields ?? []).filter((sub) => ok(sub, row[sub.id], row))).length;
    }, 0);
    return (answered / (answered + missingAll(a).length)) * 100;
}

/** Field ids a save may carry; anything else is refused by the API. */
export const FIELD_IDS = new Set(SECTIONS.flatMap((s) => s.fields.map((f) => f.id)));

/** Section state for the rail: done (nothing required missing and something given), partial, or empty. */
export function sectionState(section: Section, a: Answers): "done" | "partial" | "empty" {
    const any = section.fields.some((f) => visible(f, a) && filled(a[f.id]));
    if (!any) return "empty";
    return missingIn(section, a).length === 0 ? "done" : "partial";
}

export { filled };
